// src/context/ToastContext.tsx — Global toast notification system
// Provides success/error/warning/info toasts with auto-dismiss and animation
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// ─── Types ──────────────────────────────────────
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration: number;
}

interface ToastContextValue {
    toasts: Toast[];
    addToast: (type: ToastType, title: string, message?: string, duration?: number) => void;
    success: (title: string, message?: string) => void;
    error: (title: string, message?: string) => void;
    warning: (title: string, message?: string) => void;
    info: (title: string, message?: string) => void;
    removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// ─── Config ─────────────────────────────────────
const TOAST_CONFIG = {
    success: { icon: CheckCircle2, accent: 'border-green/40', iconWrap: 'bg-green/10', iconColor: 'text-green' },
    error: { icon: XCircle, accent: 'border-red/40', iconWrap: 'bg-red/10', iconColor: 'text-red' },
    warning: { icon: AlertTriangle, accent: 'border-amber/40', iconWrap: 'bg-amber/10', iconColor: 'text-amber' },
    info: { icon: Info, accent: 'border-indigo/40', iconWrap: 'bg-indigo/10', iconColor: 'text-indigo' }
};

// ─── Toast Component ────────────────────────────
function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
    const config = TOAST_CONFIG[toast.type];
    const Icon = config.icon;

    React.useEffect(() => {
        const timer = setTimeout(() => onRemove(toast.id), toast.duration);
        return () => clearTimeout(timer);
    }, [toast.id, toast.duration, onRemove]);

    return (
        <div
            className={`flex w-full max-w-md items-start gap-3 rounded-2xl border bg-card p-4 shadow-2xl shadow-black/30 ${config.accent} animate-slide-in-right`}
            role={toast.type === 'error' ? 'alert' : 'status'}
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
        >
            <span className={`flex-shrink-0 rounded-xl p-2 ${config.iconWrap}`}>
                <Icon className={`h-5 w-5 ${config.iconColor}`} aria-hidden="true" />
            </span>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-primary">{toast.title}</p>
                {toast.message && <p className="mt-1 text-sm leading-relaxed text-secondary">{toast.message}</p>}
            </div>
            <button type="button" onClick={() => onRemove(toast.id)} aria-label={`Dismiss ${toast.title}`} className="rounded-lg p-1.5 text-muted transition hover:bg-hover hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo">
                <X className="w-4 h-4" aria-hidden="true" />
            </button>
        </div>
    );
}

// ─── Provider ───────────────────────────────────
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const addToast = useCallback((type: ToastType, title: string, message?: string, duration: number = 4000) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setToasts(prev => [...prev.slice(-4), { id, type, title, message, duration }]); // Max 5 toasts
    }, []);

    const success = useCallback((t: string, m?: string) => addToast('success', t, m), [addToast]);
    const error = useCallback((t: string, m?: string) => addToast('error', t, m, 6000), [addToast]);
    const warning = useCallback((t: string, m?: string) => addToast('warning', t, m, 5000), [addToast]);
    const info = useCallback((t: string, m?: string) => addToast('info', t, m), [addToast]);

    return (
        <ToastContext.Provider value={{ toasts, addToast, success, error, warning, info, removeToast }}>
            {children}
            {/* Fixed popup stack stays visible regardless of page scroll. */}
            <div className="pointer-events-none fixed bottom-4 left-4 right-4 z-[9999] flex flex-col items-end gap-3 sm:left-auto sm:w-[26rem]" aria-label="Notifications">
                {toasts.map(toast => (
                    <div key={toast.id} className="pointer-events-auto">
                        <ToastItem toast={toast} onRemove={removeToast} />
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export function useToast(): ToastContextValue {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
